import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../lib/auth";
import { PHONE, PHONE_HREF, EMAIL } from "../lib/constants";

const NAV = [
  ["/investors", "For Investors"],
  ["/opportunities", "Opportunities"],
  ["/distressed", "Distressed"],
  ["/sell", "Sell a Property"],
  ["/about", "About"],
  ["/contact", "Contact"],
];

const AccountLink = ({ className, onClick }) => {
  const { user } = useAuth();
  const to = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/login";
  return (
    <Link to={to} onClick={onClick} className={className} data-testid="nav-account-link">
      {user ? (user.role === "admin" ? "Admin" : "Portal") : "Investor Login"}
    </Link>
  );
};

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const overHero = pathname === "/" && !scrolled;
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 40);
    f();
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);
  useEffect(() => setOpen(false), [pathname]);
  const tone = overHero ? "light" : "dark";

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${overHero ? "bg-transparent border-transparent" : "bg-paper/85 backdrop-blur-xl border-b border-navy/10"}`}
    >
      <div className="container-x flex h-[76px] items-center justify-between">
        <Link to="/" data-testid="header-home-link"><Logo tone={tone} /></Link>
        <nav className="hidden xl:flex items-center gap-8">
          {NAV.map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-link-${to.slice(1)}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors ${overHero ? "text-paper/80 hover:text-paper" : "text-navy/70 hover:text-navy"} ${isActive ? "!text-gold" : ""}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-5">
          <AccountLink className={`text-[13px] font-medium ${overHero ? "text-paper/80 hover:text-paper" : "text-navy/70 hover:text-navy"}`} />
          <Link to="/investors#criteria" className={overHero ? "btn-gold !py-2.5" : "btn-primary !py-2.5"} data-testid="header-cta-criteria">
            Tell Us What You're Looking For
          </Link>
        </div>
        <button className={`xl:hidden p-2 ${overHero ? "text-paper" : "text-navy"}`} onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </button>
      </div>
      {open && (
        <div className="fixed inset-0 z-[60] bg-navy text-paper xl:hidden flex flex-col" data-testid="mobile-menu">
          <div className="container-x flex h-[76px] items-center justify-between">
            <Logo tone="light" testid="mobile-logo" />
            <button onClick={() => setOpen(false)} className="p-2" data-testid="mobile-menu-close" aria-label="Close menu"><X className="h-6 w-6" /></button>
          </div>
          <nav className="container-x flex flex-col gap-1 pt-6">
            {NAV.map(([to, label]) => (
              <Link key={to} to={to} className="font-serif text-3xl py-2.5 border-b border-paper/10" data-testid={`mobile-nav-${to.slice(1)}`}>{label}</Link>
            ))}
            <AccountLink className="font-serif text-3xl py-2.5 text-gold" />
          </nav>
          <div className="container-x mt-auto pb-10 space-y-3">
            <Link to="/investors#criteria" className="btn-gold w-full" data-testid="mobile-menu-cta">Tell Us What You're Looking For</Link>
            <a href={PHONE_HREF} className="btn-ghost w-full text-paper" data-testid="mobile-menu-phone"><Phone className="h-4 w-4" /> {PHONE}</a>
          </div>
        </div>
      )}
    </header>
  );
}

const FOOT = [
  ["Platform", [["/investors", "For Investors"], ["/opportunities", "Opportunities"], ["/distressed", "Foreclosures & Distressed"], ["/sell", "Sell a Property"], ["/property-management", "Property Management"]]],
  ["Company", [["/about", "About"], ["/faq", "FAQ"], ["/contact", "Contact"], ["/markets/portland", "Portland CRE"], ["/markets/portland-metro", "Portland Metro"], ["/brand", "Brand Assets"]]],
  ["Legal", [["/legal/terms", "Terms of Use"], ["/legal/privacy", "Privacy Policy"], ["/legal/brokerage-disclosure", "Brokerage Disclosure"], ["/legal/investor-disclosures", "Investor Disclosures"], ["/legal/risk-disclosures", "Risk Disclosures"], ["/legal/disclaimer", "Website Disclaimer"]]],
];

function Footer() {
  return (
    <footer className="bg-navy text-paper relative grain" data-testid="site-footer">
      <div className="container-x py-20 grid gap-14 lg:grid-cols-[1.4fr_2fr]">
        <div>
          <Logo tone="light" testid="footer-logo" />
          <p className="mt-6 font-serif text-2xl leading-snug text-paper/90 max-w-sm">Your capital. Your criteria. Our local legwork.</p>
          <div className="mt-8 space-y-2 text-sm text-paper/70">
            <p className="text-paper">Abdirahman Sido · Licensed Oregon Realtor</p>
            <a href={PHONE_HREF} className="flex items-center gap-2 hover:text-gold" data-testid="footer-phone"><Phone className="h-4 w-4" />{PHONE}</a>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-gold" data-testid="footer-email"><Mail className="h-4 w-4" />{EMAIL}</a>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-10">
          {FOOT.map(([h, links]) => (
            <div key={h}>
              <p className="eyebrow mb-5">{h}</p>
              <ul className="space-y-3">
                {links.map(([to, l]) => (
                  <li key={to}><Link to={to} className="text-sm text-paper/70 hover:text-paper transition-colors" data-testid={`footer-link-${to.replace(/\//g, "-").slice(1)}`}>{l}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-paper/10">
        <p className="container-x py-8 text-[12px] leading-relaxed text-paper/50" data-testid="footer-disclaimer">
          Sido Holdings is operated by Abdirahman Sido, a licensed Oregon real estate licensee. Sido Holdings does not offer or sell securities, accept pooled investor funds, or guarantee investment returns. Property information may come from third parties and should be independently verified. Financial figures are estimates or seller-provided and are not guarantees. Past performance does not guarantee future results. Investors are responsible for their own due diligence and investment decisions. All opportunities are subject to availability and transaction conditions. © {new Date().getFullYear()} Sido Holdings.
        </p>
      </div>
    </footer>
  );
}

const MobileCta = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/investors") || pathname.startsWith("/admin") || pathname.startsWith("/dashboard")) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-navy/95 backdrop-blur-xl border-t border-paper/10 p-3 flex gap-2" data-testid="mobile-sticky-cta">
      <Link to="/investors#criteria" className="btn-gold flex-1 !px-3 !py-3 text-[13px]" data-testid="mobile-sticky-criteria">Tell Us What You Want <ArrowUpRight className="h-4 w-4" /></Link>
      <a href={PHONE_HREF} className="grid place-items-center w-12 border border-paper/30 text-paper" aria-label="Call" data-testid="mobile-sticky-call"><Phone className="h-4 w-4" /></a>
    </div>
  );
};

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pb-16 md:pb-0"><Outlet /></main>
      <Footer />
      <MobileCta />
    </div>
  );
}
